import cx from "classnames";
import { t } from "ttag";

import CS from "metabase/css/core/index.css";
import { getPulseParameters } from "metabase/lib/pulse";
import { ParametersList } from "metabase/parameters/components/ParametersList";
import { getDefaultValuePopulatedParameters } from "metabase-lib/v1/parameters/utils/parameter-values";
import type { UiParameter } from "metabase-lib/v1/parameters/types";
import type { Parameter, ParameterId } from "metabase-types/api";
import type { DraftDashboardSubscription } from "metabase-types/store";

type Props = {
  className?: string;
  parameters: UiParameter[];
  hiddenParameters?: string;
  dashboard: { id: number };
  pulse: DraftDashboardSubscription;
  setPulseParameters: (parameters: UiParameter[]) => void;
};

export const SubscriptionParametersSection = ({
  className,
  parameters,
  hiddenParameters,
  dashboard,
  pulse,
  setPulseParameters,
}: Props) => {
  // Build a map of pulse-level parameter overrides, then merge with all
  // dashboard parameters so every filter is shown (even those without a value)
  const pulseParamValues = getPulseParameters(pulse).reduce<
    Record<string, Parameter["value"]>
  >((map, p) => {
    map[p.id] = p.value;
    return map;
  }, {});
  const allParameters = getDefaultValuePopulatedParameters(
    parameters,
    pulseParamValues,
  );

  const setParameterValue = (id: ParameterId, value: Parameter["value"]) => {
    const param = parameters.find(p => p.id === id);
    if (!param) {
      return;
    }
    const others = getPulseParameters(pulse).filter(p => p.id !== id);
    const updated =
      value == null ? others : [...others, { ...param, value }];
    setPulseParameters(updated);
  };

  // Remove the pulse-level override so the dashboard default is used instead
  const setParameterValueToDefault = (id: ParameterId) => {
    const others = getPulseParameters(pulse).filter(p => p.id !== id);
    setPulseParameters(others);
  };

  if (!allParameters.length) {
    return null;
  }

  return (
    <div className={cx(className)}>
      <h4 className={CS.mb2}>{t`Choose filter values for this subscription`}</h4>
      <ParametersList
        className={cx(CS.alignStretch, CS.rowGap1)}
        vertical
        dashboardId={dashboard?.id}
        parameters={allParameters}
        hideParameters={hiddenParameters}
        setParameterValue={setParameterValue}
        setParameterValueToDefault={setParameterValueToDefault}
      />
    </div>
  );
};
